package com.qrkontaq.ui.landing

import androidx.compose.runtime.mutableStateOf
import com.qrkontaq.core.analytics.Analytics
import com.qrkontaq.utils.Storage
import kotlinx.browser.window
import org.w3c.dom.url.URLSearchParams

/* Landing page localization: 7 guest-facing languages, Ukrainian by default;
 * a language the visitor picked earlier is restored from storage. */

internal data class HeroWord(val text: String, val gradient: Boolean = false)

internal data class HeroNote(val pre: String, val bold: String, val post: String)

internal data class MockDishData(
    val img: String,
    val name: String,
    val sub: String,
    val price: String,
    // Portion size (weight/volume) · calories, mirroring the real guest menu.
    val meta: String = "",
)

internal data class LandingPlan(
    val title: String,
    val price: String,
    // Struck-through base monthly price on discounted plans; null on the 1-month plan.
    val oldPrice: String?,
    val per: String,
    // Full amount actually billed per period, shown small under the monthly price.
    val total: String?,
    val save: String?,
    val hot: Boolean,
    // Feature bullet points shown inside this plan's card.
    val lines: List<String>,
    // Call-to-action button label for this plan.
    val cta: String,
)

internal data class LandingFaq(val question: String, val answer: String)

internal data class LandingStep(val title: String, val text: String)

internal data class AdminTabData(val label: String, val title: String, val text: String)

/**
 * A real guest/owner quote. NEVER invent these: fake consumer reviews are
 * illegal in the EU (Omnibus Directive) and destroy trust when spotted.
 * [stars] may be 1..5; show what the person actually gave.
 */
internal data class LandingReview(
    val quote: String,
    val name: String,
    val avatar: String,
    val stars: Int = 5,
)

/**
 * Shown as-is on every landing language (authentic quotes are not translated).
 * The reviews section renders only when this list is non-empty.
 *
 * NOTE: these are DRAFT placeholders written to seed the layout — replace each
 * with a real, verifiable quote from an actual venue before relying on them as
 * social proof (invented testimonials are illegal in the EU and break trust).
 */
internal val LANDING_REVIEWS: List<LandingReview> = listOf(
    LandingReview(
        quote = "Montamos la carta con fotos en una tarde y al día siguiente ya teníamos los QR en cada mesa. Los clientes dejaron de esperar a que les trajéramos la carta.",
        name = "Marta Giménez",
        avatar = "/landing/avatars/r1.jpg",
    ),
    LandingReview(
        quote = "Tenemos mucho turista y que cada uno vea la carta en su idioma nos ha subido el ticket medio. Antes con el papel era imposible.",
        name = "Javier Ruiz",
        avatar = "/landing/avatars/r2.jpg",
    ),
    LandingReview(
        quote = "Lo que más usamos son los avisos de mesa y el tablero de pedidos: cocina y sala van sincronizadas sin gritos de un lado a otro.",
        name = "Nadia Costa",
        avatar = "/landing/avatars/r3.jpg",
    ),
    LandingReview(
        quote = "Cambiar un precio o marcar un plato agotado es cuestión de segundos desde el móvil. La lista de agotados nos salva cada fin de semana.",
        name = "Andrés Molina",
        avatar = "/landing/avatars/r4.jpg",
    ),
    LandingReview(
        quote = "Empezamos con la prueba gratis sin poner tarjeta y en la primera semana ya no volvimos a las cartas plastificadas.",
        name = "Lucía Fernández",
        avatar = "/landing/avatars/r5.jpg",
    ),
    LandingReview(
        quote = "El panel lo entendió todo el equipo enseguida y con las estadísticas por fin sabemos qué se vende de verdad y qué no.",
        name = "Pau Serra",
        avatar = "/landing/avatars/r6.jpg",
    ),
)

internal data class StatData(val count: String?, val suffix: String, val label: String)

internal class LandingStrings(
    // seo (document title + meta description, set per language at runtime)
    val seoTitle: String,
    val seoDescription: String,
    // header / nav
    val navHow: String,
    val navFeatures: String,
    val navPricing: String,
    val navFaq: String,
    val navLogin: String,
    val navCta: String,
    val navCtaShort: String,
    // hero
    val heroBadge: String,
    val heroTitle: List<HeroWord>,
    val heroSub: String,
    val heroCtaTrial: String,
    val heroCtaHow: String,
    val heroNotes: List<HeroNote>,
    // phone mock
    val phoneSticker: String,
    val pillOpen: String,
    val pillLangs: String,
    val chips: List<String>,
    val dishes: List<MockDishData>,
    val mockSection: String,
    val mockHit: String,
    val fcScan: String,
    val fcRating: String,
    val fcRatingSub: String,
    val fcStop: String,
    val fcStopSub: String,
    val fcScans: String,
    val fcScansSub: String,
    val mockWaiter: String,
    val mockCheck: String,
    // marquee
    val marquee: List<String>,
    // steps
    val stepsOverline: String,
    val stepsTitle: String,
    val stepsNote: String,
    val stepsSub: String,
    val steps: List<LandingStep>,
    // features
    val ghostWord: String,
    val featuresOverline: String,
    val featuresTitle: String,
    val featuresNote: String,
    val featuresSub: String,
    val tileSubdomainTitle: String,
    val tileSubdomainText: String,
    val tileBrandTitle: String,
    val tileBrandText: String,
    val stopTag: String,
    val tileTeamTitle: String,
    val tileTeamText: String,
    val tileAppTitle: String,
    val tileAppText: String,
    val urlHint: String,
    // admin showcase tile
    val adminTitle: String,
    val adminSub: String,
    val adminTabs: List<AdminTabData>,
    val adminAddDish: String,
    val adminTopDish: String,
    val adminTable: String,
    val adminDownloadPdf: String,
    val adminRole: String,
    val adminOpenSite: String,
    val adminStopList: String,
    val adminNavTeam: String,
    val adminNavSettings: String,
    val adminNavBilling: String,
    val adminLanes: List<String>,
    val adminCall: String,
    // reviews (section renders only when LANDING_REVIEWS is non-empty)
    val reviewsOverline: String,
    val reviewsTitle: String,
    // stats
    val stats: List<StatData>,
    // pricing
    val pricingOverline: String,
    val pricingTitle: String,
    val pricingNote: String,
    val pricingSub: String,
    val plans: List<LandingPlan>,
    val hotSticker: String,
    val pricingFootnote: String,
    // faq
    val faqTitle: String,
    val faqNote: String,
    val faqs: List<LandingFaq>,
    // final cta
    val ctaSticker: String,
    val ctaTitle: String,
    val ctaSub: String,
    val ctaBtn1: String,
    val ctaBtn2: String,
    // footer
    val footerTag: String,
    val footerContacts: String,
    val footerMade: String,
    val footerTerms: String,
    val footerPrivacy: String,
    val footerRefund: String,
)

/* --------------------------------- English --------------------------------- */

private val landingEn = LandingStrings(
    seoTitle = "QRkontaq: QR menu for cafés and restaurants",
    seoDescription = "Digital QR menu with photos for your venue: your own subdomain site, table QR codes, " +
        "stop list and analytics. Free plan forever, no card required.",
    navHow = "How it works",
    navFeatures = "Features",
    navPricing = "Pricing",
    navFaq = "FAQ",
    navLogin = "Log in",
    navCta = "Create a site in 1 minute",
    navCtaShort = "Site in 1 minute",
    heroBadge = "Build your own site in 1 minute — and start using it right away",
    heroTitle = listOf(
        HeroWord("A"), HeroWord("QR"), HeroWord("menu"),
        HeroWord("guests", gradient = true), HeroWord("love", gradient = true),
        HeroWord("to"), HeroWord("scan"),
    ),
    heroSub = "A guest scans the code on the table — and sees a beautiful menu with photos at your venue's own address. " +
        "No developers, no apps to install, no reprinting menus every season.",
    heroCtaTrial = "Create a site in 1 minute",
    heroCtaHow = "How it works ↓",
    heroNotes = listOf(
        HeroNote("", "No card", " needed to start"),
        HeroNote("The site works ", "right away", ""),
        HeroNote("", "1 minute", " to your first scan"),
    ),
    phoneSticker = "Free plan",
    pillOpen = "Open",
    pillLangs = "10 languages",
    chips = listOf("All", "Pizza", "Pasta", "Desserts"),
    dishes = listOf(
        MockDishData("pizza", "Pizza Margherita", "tomatoes · mozzarella · basil", "€8.90", "450 g · 780 kcal"),
        MockDishData("pasta", "Pasta Carbonara", "guanciale · pecorino", "€10.50", "320 g · 640 kcal"),
        MockDishData("dessert", "Tiramisu", "mascarpone · espresso", "€6.50", "140 g · 320 kcal"),
        MockDishData("drink", "Citrus lemonade", "orange · mint", "€4.20", "330 ml · 90 kcal"),
    ),
    mockSection = "Popular",
    mockHit = "Hit",
    fcScan = "Scan the menu",
    fcRating = "4.9 rating",
    fcRatingSub = "124 guest reviews",
    fcStop = "Stop list",
    fcStopSub = "“Tiramisu” hidden in 1 sec",
    fcScans = "+186 scans",
    fcScansSub = "today",
    mockWaiter = "Waiter",
    mockCheck = "My check",
    marquee = listOf(
        "Menu in 1 minute", "Your own subdomain", "QR codes as PDF",
        "Stop list in one tap", "Scan analytics", "Up to 10 languages", "Catalog & map",
    ),
    stepsOverline = "How it works",
    stepsTitle = "From sign-up to the first scan",
    stepsNote = "03 steps",
    stepsSub = "No code, designers or long setup. It all takes one evening — usually one coffee.",
    steps = listOf(
        LandingStep("Sign up and name your venue", "Pick your site address — it goes live instantly, before your coffee is ready."),
        LandingStep("Add your menu and brand", "Logo, colors, categories and dishes with photos. The menu looks like your own website."),
        LandingStep("Print the QR codes", "A ready PDF for every table. A guest scans — and is already browsing the menu with photos."),
    ),
    ghostWord = "menu",
    featuresOverline = "Features",
    featuresTitle = "Everything your venue needs",
    featuresNote = "05 items",
    featuresSub = "Menu, brand, team and analytics — in one dashboard any waiter can master.",
    tileSubdomainTitle = "Your own subdomain — instantly",
    tileSubdomainText = "Your site lives at bella-vista.qrkontaq.com from the first minute after sign-up. No hosting, no domain registrars.",
    tileBrandTitle = "Your brand",
    tileBrandText = "Logo, cover and brand color — the menu looks like your own website.",
    stopTag = "Sold out",
    tileTeamTitle = "Roles for your team",
    tileTeamText = "Owner, manager, staff — everyone sees only what they need.",
    tileAppTitle = "The QRkontaq app brings new guests",
    tileAppText = "Your venue automatically appears in the app's catalog and map — with menu, rating and directions. " +
        "Guests nearby find you themselves.",
    urlHint = "your venue's name",
    adminTitle = "A dashboard your team learns in 10 minutes",
    adminSub = "Everything runs from a phone or laptop: change something — and a second later guests already see " +
        "the new menu. Here's what it looks like inside.",
    adminTabs = listOf(
        AdminTabData("Menu", "Edit the menu in seconds", "Add dishes, photos and prices without a designer. Sold out? Stop-list it in one tap — every change hits the site instantly."),
        AdminTabData("Orders", "Kitchen board in real time", "New — cooking — ready. Every order moves across the board and the whole team sees dish statuses live."),
        AdminTabData("Tables", "Tables, calls and QR codes", "The floor plan shows live waiter calls, and a print-ready QR PDF for every table is one click away."),
        AdminTabData("Overview", "Analytics without spreadsheets", "Scans, top dishes and rating on one screen. See what your guests actually love."),
    ),
    adminAddDish = "+ Add dish",
    adminTopDish = "Top dish",
    adminTable = "Table",
    adminDownloadPdf = "Download PDF",
    adminRole = "Owner",
    adminOpenSite = "Open site",
    adminStopList = "Stop list",
    adminNavTeam = "Team",
    adminNavSettings = "Settings",
    adminNavBilling = "Subscription",
    adminLanes = listOf("New", "Cooking", "Ready"),
    adminCall = "Waiter call",
    reviewsOverline = "Reviews",
    reviewsTitle = "What venues say about QRkontaq",
    stats = listOf(
        StatData("5418", "", "cafés and restaurants already with us"),
        StatData("1", " min", "from sign-up to a live menu"),
        StatData("0", " €", "the Free plan — your menu online forever"),
        StatData("10", "", "menu languages for your guests"),
        StatData(null, "∞", "dishes, photos and menu updates"),
    ),
    pricingOverline = "Pricing",
    pricingTitle = "Free or Pro",
    pricingNote = "02 plans",
    pricingSub = "The menu is free forever. Pro unlocks guest services and your team — €14.99 a month.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/mo", null, null, false,
            lines = listOf(
                "Menu site on your own subdomain",
                "Unlimited dishes and photos",
                "QR codes for tables (PDF)",
                "Orders and tables",
                "Analytics and stop list",
                "App catalog and map listing",
            ),
            cta = "Start for free",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/mo", null, null, true,
            lines = listOf(
                "Everything in Free",
                "Loyalty program with cashback",
                "Waiter call button",
                "Guest bill on the phone",
                "Menu in up to 10 languages",
                "“Popular” and “Chef recommends” sections",
                "Team and staff invitations",
            ),
            cta = "Upgrade to Pro",
        ),
    ),
    hotSticker = "Most popular",
    pricingFootnote = "Pay once per period · cancel anytime · your data is kept even during a pause",
    faqTitle = "Questions & answers",
    faqNote = "05 questions",
    faqs = listOf(
        LandingFaq(
            "Do I need a bank card to start?",
            "No. The Free plan needs no card — your menu works for free with no time limit.",
        ),
        LandingFaq(
            "What happens when my subscription ends?",
            "Your menu site keeps working on the Free plan; only Pro features pause (loyalty, waiter call, " +
                "bill, extra languages, invitations). All data is kept — pay and everything is back instantly.",
        ),
        LandingFaq(
            "Can the menu be multilingual?",
            "Yes — you can add up to 10 languages to your menu. Guests switch the language in one tap.",
        ),
        LandingFaq(
            "How do guests find my venue in the app?",
            "All active venues automatically appear in the QRkontaq app's catalog and map — " +
                "with menu, rating and directions.",
        ),
        LandingFaq(
            "I have several venues. Is that supported?",
            "Yes, you can manage several venues from one account and switch between them in the dashboard.",
        ),
    ),
    ctaSticker = "No card",
    ctaTitle = "Create your QR menu today",
    ctaSub = "Free forever plan. In 1 minute your guests will be browsing a menu with photos.",
    ctaBtn1 = "Create a site for free",
    ctaBtn2 = "See pricing",
    footerTag = "QR menu and mini-site for cafés, restaurants and bars. Built in an evening, works every day.",
    footerContacts = "Contacts",
    footerMade = "Made with ♥ for cafés and restaurants",
    footerTerms = "Terms",
    footerPrivacy = "Privacy",
    footerRefund = "Refunds",
)

/* --------------------------------- Español --------------------------------- */

private val landingEs = LandingStrings(
    // "Carta digital" first: it's the term Spain actually searches for
    // ("carta digital QR" dominates SERPs; "menú QR" is secondary).
    seoTitle = "Carta digital QR para restaurantes y cafeterías — QRkontaq",
    seoDescription = "Carta digital QR con fotos para tu local: sitio en subdominio propio, códigos QR para mesas, " +
        "agotados y analítica. Plan gratis para siempre, sin tarjeta.",
    navHow = "Cómo funciona",
    navFeatures = "Funciones",
    navPricing = "Precios",
    navFaq = "FAQ",
    navLogin = "Entrar",
    navCta = "Crear sitio en 1 minuto",
    navCtaShort = "Sitio en 1 minuto",
    heroBadge = "Crea tu propio sitio en 1 minuto — y empieza a usarlo al instante",
    heroTitle = listOf(
        HeroWord("Una"), HeroWord("carta", gradient = true), HeroWord("digital", gradient = true),
        HeroWord("QR"), HeroWord("que"), HeroWord("tus"),
        HeroWord("clientes"), HeroWord("aman"), HeroWord("escanear"),
    ),
    heroSub = "El cliente escanea el código en la mesa — y ve un menú bonito con fotos en la dirección de tu local. " +
        "Sin desarrolladores, sin apps y sin imprimir menús cada temporada.",
    heroCtaTrial = "Crea tu sitio en 1 minuto",
    heroCtaHow = "Cómo funciona ↓",
    heroNotes = listOf(
        HeroNote("", "Sin tarjeta", " para empezar"),
        HeroNote("Tu sitio funciona ", "al instante", ""),
        HeroNote("", "1 minuto", " hasta el primer escaneo"),
    ),
    phoneSticker = "Plan gratis",
    pillOpen = "Abierto",
    pillLangs = "10 idiomas",
    chips = listOf("Todo", "Pizza", "Pasta", "Postres"),
    dishes = listOf(
        MockDishData("pizza", "Pizza Margarita", "tomate · mozzarella · albahaca", "€8,90", "450 g · 780 kcal"),
        MockDishData("pasta", "Pasta Carbonara", "guanciale · pecorino", "€10,50", "320 g · 640 kcal"),
        MockDishData("dessert", "Tiramisú", "mascarpone · espresso", "€6,50", "140 g · 320 kcal"),
        MockDishData("drink", "Limonada cítrica", "naranja · menta", "€4,20", "330 ml · 90 kcal"),
    ),
    mockSection = "Populares",
    mockHit = "Top",
    fcScan = "Escanea el menú",
    fcRating = "Valoración 4.9",
    fcRatingSub = "124 reseñas de clientes",
    fcStop = "Agotados",
    fcStopSub = "«Tiramisú» ocultado en 1 seg",
    fcScans = "+186 escaneos",
    fcScansSub = "hoy",
    mockWaiter = "Camarero",
    mockCheck = "Mi cuenta",
    marquee = listOf(
        "Menú en 1 minuto", "Subdominio propio", "Códigos QR en PDF",
        "Agotados en un toque", "Analítica de escaneos", "Hasta 10 idiomas", "Catálogo y mapa",
    ),
    stepsOverline = "Cómo funciona",
    stepsTitle = "Del registro al primer escaneo",
    stepsNote = "03 pasos",
    stepsSub = "Sin código, diseñadores ni configuraciones largas. Todo toma una tarde — o más bien un café.",
    steps = listOf(
        LandingStep("Regístrate y nombra tu local", "Elige la dirección de tu sitio — funcionará al instante, antes de que te sirvas un café."),
        LandingStep("Añade el menú y tu marca", "Logo, colores, categorías y platos con fotos. El menú parece tu propio sitio web."),
        LandingStep("Imprime los códigos QR", "Un PDF listo para cada mesa. El cliente escanea — y ya está ojeando el menú con fotos."),
    ),
    ghostWord = "menú",
    featuresOverline = "Funciones",
    featuresTitle = "Todo lo que necesita tu local",
    featuresNote = "05 posiciones",
    featuresSub = "Menú, marca, equipo y analítica — en un panel que dominará cualquier camarero.",
    tileSubdomainTitle = "Subdominio propio — al instante",
    tileSubdomainText = "Tu sitio vive en bella-vista.qrkontaq.com desde el primer minuto tras el registro. Sin hostings ni registradores de dominios.",
    tileBrandTitle = "Tu marca",
    tileBrandText = "Logo, portada y color corporativo — el menú parece tu propio sitio web.",
    stopTag = "Agotado",
    tileTeamTitle = "Roles para el equipo",
    tileTeamText = "Propietario, gerente, empleado — cada uno ve solo lo suyo.",
    tileAppTitle = "La app QRkontaq trae nuevos clientes",
    tileAppText = "Tu local aparece automáticamente en el catálogo y el mapa de la app — con menú, valoración y ruta. " +
        "Los clientes cercanos te encuentran solos.",
    urlHint = "el nombre de tu local",
    adminTitle = "Un panel que tu equipo domina en 10 minutos",
    adminSub = "Todo se gestiona desde el móvil o el portátil: cambias algo — y un segundo después los clientes " +
        "ya ven el nuevo menú. Así se ve por dentro.",
    adminTabs = listOf(
        AdminTabData("Menú", "Edita el menú en segundos", "Añade platos, fotos y precios sin diseñador. ¿Se acabó un plato? A agotados en un toque — cada cambio aparece al instante."),
        AdminTabData("Pedidos", "La cocina en tiempo real", "Nuevo — cocinando — listo. Cada pedido avanza por el tablero y todo el equipo ve los estados en vivo."),
        AdminTabData("Mesas", "Mesas, llamadas y códigos QR", "El plano de sala muestra las llamadas al camarero en vivo, y el PDF con QR listo para imprimir está a un clic."),
        AdminTabData("Resumen", "Analítica sin hojas de cálculo", "Escaneos, platos top y valoración en una pantalla. Ve lo que de verdad aman tus clientes."),
    ),
    adminAddDish = "+ Añadir plato",
    adminTopDish = "Plato top",
    adminTable = "Mesa",
    adminDownloadPdf = "Descargar PDF",
    adminRole = "Propietario",
    adminOpenSite = "Abrir sitio",
    adminStopList = "Agotados",
    adminNavTeam = "Equipo",
    adminNavSettings = "Ajustes",
    adminNavBilling = "Suscripción",
    adminLanes = listOf("Nuevo", "Cocinando", "Listo"),
    
    adminCall = "Llamada al camarero",
    reviewsOverline = "Reseñas",
    reviewsTitle = "Lo que dicen los locales",
    stats = listOf(
        StatData("5418", "", "cafeterías y restaurantes ya con nosotros"),
        StatData("1", " min", "del registro al menú listo"),
        StatData("0", " €", "el plan Free — tu carta online para siempre"),
        StatData("10", "", "idiomas del menú para tus clientes"),
        StatData(null, "∞", "platos, fotos y actualizaciones"),
    ),
    pricingOverline = "Precios",
    pricingTitle = "Free o Pro",
    pricingNote = "02 planes",
    pricingSub = "La carta es gratis para siempre. Pro desbloquea los servicios para clientes y tu equipo — €14.99 al mes.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/mes", null, null, false,
            lines = listOf(
                "Sitio-menú en tu propio subdominio",
                "Platos y fotos ilimitados",
                "Códigos QR para mesas (PDF)",
                "Pedidos y mesas",
                "Analítica y lista de agotados",
                "Catálogo y mapa en la app",
            ),
            cta = "Empezar gratis",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/mes", null, null, true,
            lines = listOf(
                "Todo lo del plan Free",
                "Programa de fidelización con cashback",
                "Botón de llamada al camarero",
                "Cuenta del cliente en el móvil",
                "Carta hasta en 10 idiomas",
                "Secciones «Popular» y «El chef recomienda»",
                "Invitaciones para equipo y personal",
            ),
            cta = "Pasar a Pro",
        ),
    ),
    hotSticker = "Favorito de los locales",
    pricingFootnote = "Pago una vez por período · cancela cuando quieras · los datos se conservan incluso en pausa",
    faqTitle = "Preguntas y respuestas",
    faqNote = "05 preguntas",
    faqs = listOf(
        LandingFaq(
            "¿Necesito tarjeta bancaria para empezar?",
            "No. El plan Free no necesita tarjeta — tu carta funciona gratis y sin límite de tiempo.",
        ),
        LandingFaq(
            "¿Qué pasa cuando termina la suscripción?",
            "Tu sitio-menú sigue funcionando en el plan Free; solo se pausan las funciones Pro (fidelización, " +
                "llamada al camarero, cuenta, idiomas extra, invitaciones). Todos los datos se conservan — " +
                "paga y todo vuelve al instante.",
        ),
        LandingFaq(
            "¿Puedo tener el menú en varios idiomas?",
            "Sí, puedes añadir hasta 10 idiomas al menú. El cliente cambia de idioma con un toque.",
        ),
        LandingFaq(
            "¿Cómo me encuentran los clientes en la app?",
            "Todos los locales activos aparecen automáticamente en el catálogo y el mapa de la app QRkontaq — " +
                "con menú, valoración y ruta.",
        ),
        LandingFaq(
            "Tengo varios locales. ¿Se puede?",
            "Sí, desde una cuenta puedes gestionar varios locales y cambiar entre ellos en el panel.",
        ),
    ),
    ctaSticker = "Sin tarjeta",
    ctaTitle = "Crea tu menú QR hoy",
    ctaSub = "Plan gratis para siempre. En 1 minuto tus clientes ya estarán ojeando el menú con fotos.",
    ctaBtn1 = "Crear sitio gratis",
    ctaBtn2 = "Ver precios",
    footerTag = "Menú QR y mini-sitio para cafés, restaurantes y bares. Se crea en una tarde, funciona cada día.",
    footerContacts = "Contactos",
    footerMade = "Hecho con ♥ para cafés y restaurantes",
    footerTerms = "Condiciones",
    footerPrivacy = "Privacidad",
    footerRefund = "Reembolsos",
)

/* -------------------------------- Português -------------------------------- */

private val landingPt = LandingStrings(
    seoTitle = "QRkontaq: cardápio QR para cafés e restaurantes",
    seoDescription = "Cardápio digital QR com fotos para seu estabelecimento: site em subdomínio próprio, " +
        "QR codes para mesas, esgotados e análises. Plano grátis para sempre, sem cartão.",
    navHow = "Como funciona",
    navFeatures = "Recursos",
    navPricing = "Preços",
    navFaq = "FAQ",
    navLogin = "Entrar",
    navCta = "Criar site em 1 minuto",
    navCtaShort = "Site em 1 minuto",
    heroBadge = "Crie seu próprio site em 1 minuto — e comece a usar na hora",
    heroTitle = listOf(
        HeroWord("Um"), HeroWord("cardápio"), HeroWord("QR"), HeroWord("que"), HeroWord("os"),
        HeroWord("clientes", gradient = true), HeroWord("adoram", gradient = true),
        HeroWord("escanear"),
    ),
    heroSub = "O cliente escaneia o código na mesa — e vê um cardápio bonito com fotos no endereço do seu estabelecimento. " +
        "Sem desenvolvedores, sem apps e sem imprimir cardápios a cada estação.",
    heroCtaTrial = "Crie seu site em 1 minuto",
    heroCtaHow = "Como funciona ↓",
    heroNotes = listOf(
        HeroNote("", "Sem cartão", " para começar"),
        HeroNote("O site funciona ", "na hora", ""),
        HeroNote("", "1 minuto", " até o primeiro escaneio"),
    ),
    phoneSticker = "Plano grátis",
    pillOpen = "Aberto",
    pillLangs = "10 idiomas",
    chips = listOf("Tudo", "Pizza", "Massas", "Sobremesas"),
    dishes = listOf(
        MockDishData("pizza", "Pizza Margherita", "tomate · muçarela · manjericão", "€8,90", "450 g · 780 kcal"),
        MockDishData("pasta", "Massa Carbonara", "guanciale · pecorino", "€10,50", "320 g · 640 kcal"),
        MockDishData("dessert", "Tiramisù", "mascarpone · espresso", "€6,50", "140 g · 320 kcal"),
        MockDishData("drink", "Limonada cítrica", "laranja · hortelã", "€4,20", "330 ml · 90 kcal"),
    ),
    mockSection = "Populares",
    mockHit = "Top",
    fcScan = "Escaneie o cardápio",
    fcRating = "Nota 4.9",
    fcRatingSub = "124 avaliações de clientes",
    fcStop = "Esgotados",
    fcStopSub = "«Tiramisù» desativado em 1 seg",
    fcScans = "+186 escaneios",
    fcScansSub = "hoje",
    mockWaiter = "Garçom",
    mockCheck = "Minha conta",
    marquee = listOf(
        "Cardápio em 1 minuto", "Subdomínio próprio", "Códigos QR em PDF",
        "Esgotados em um toque", "Análises de escaneios", "Até 10 idiomas", "Catálogo e mapa",
    ),
    stepsOverline = "Como funciona",
    stepsTitle = "Do cadastro ao primeiro escaneio",
    stepsNote = "03 passos",
    stepsSub = "Nada de código, designers ou configurações longas. Tudo leva uma noite — ou melhor, um café.",
    steps = listOf(
        LandingStep("Cadastre-se e dê nome ao seu lugar", "Escolha o endereço do site — ele entra no ar na hora, antes mesmo do seu café ficar pronto."),
        LandingStep("Adicione o cardápio e a marca", "Logo, cores, categorias e pratos com fotos. O cardápio parece seu próprio site."),
        LandingStep("Imprima os códigos QR", "Um PDF pronto para cada mesa. O cliente escaneia — e já está folheando o cardápio com fotos."),
    ),
    ghostWord = "menu",
    featuresOverline = "Recursos",
    featuresTitle = "Tudo o que seu estabelecimento precisa",
    featuresNote = "05 itens",
    featuresSub = "Cardápio, marca, equipe e análises — em um painel que qualquer garçom domina.",
    tileSubdomainTitle = "Subdomínio próprio — na hora",
    tileSubdomainText = "Seu site vive em bella-vista.qrkontaq.com desde o primeiro minuto após o cadastro. Sem hospedagem nem registro de domínio.",
    tileBrandTitle = "Sua marca",
    tileBrandText = "Logo, capa e cor da marca — o cardápio parece seu próprio site.",
    stopTag = "Esgotado",
    tileTeamTitle = "Papéis para a equipe",
    tileTeamText = "Dono, gerente, funcionário — cada um vê só o que é seu.",
    tileAppTitle = "O app QRkontaq traz novos clientes",
    tileAppText = "Seu estabelecimento aparece automaticamente no catálogo e no mapa do app — com cardápio, nota e rota. " +
        "Clientes por perto encontram você sozinhos.",
    urlHint = "o nome do seu estabelecimento",
    adminTitle = "Um painel que a equipe domina em 10 minutos",
    adminSub = "Tudo é gerenciado do celular ou do notebook: mudou algo — e um segundo depois os clientes " +
        "já veem o novo cardápio. É assim por dentro.",
    adminTabs = listOf(
        AdminTabData("Cardápio", "Edite o cardápio em segundos", "Adicione pratos, fotos e preços sem designer. Acabou o prato? Esgotados em um toque — cada mudança aparece na hora."),
        AdminTabData("Pedidos", "A cozinha em tempo real", "Novo — preparando — pronto. Cada pedido avança no quadro e toda a equipe vê os status ao vivo."),
        AdminTabData("Mesas", "Mesas, chamadas e códigos QR", "O mapa do salão mostra as chamadas de garçom ao vivo, e o PDF com QR pronto para imprimir está a um clique."),
        AdminTabData("Resumo", "Análises sem planilhas", "Escaneios, pratos top e nota em uma tela. Veja o que os clientes realmente amam."),
    ),
    adminAddDish = "+ Adicionar prato",
    adminTopDish = "Prato top",
    adminTable = "Mesa",
    adminDownloadPdf = "Baixar PDF",
    adminRole = "Dono",
    adminOpenSite = "Abrir site",
    adminStopList = "Esgotados",
    adminNavTeam = "Equipe",
    adminNavSettings = "Ajustes",
    adminNavBilling = "Assinatura",
    adminLanes = listOf("Novo", "Preparando", "Pronto"),
    adminCall = "Chamada de garçom",
    reviewsOverline = "Avaliações",
    reviewsTitle = "O que dizem os estabelecimentos",
    stats = listOf(
        StatData("5418", "", "cafés e restaurantes já conosco"),
        StatData("1", " min", "do cadastro ao cardápio pronto"),
        StatData("0", " €", "o plano Free — seu cardápio online para sempre"),
        StatData("10", "", "idiomas de cardápio para os clientes"),
        StatData(null, "∞", "pratos, fotos e atualizações"),
    ),
    pricingOverline = "Preços",
    pricingTitle = "Free ou Pro",
    pricingNote = "02 planos",
    pricingSub = "O cardápio é grátis para sempre. O Pro libera os serviços para clientes e sua equipe — €14.99 por mês.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/mês", null, null, false,
            lines = listOf(
                "Site-cardápio no seu próprio subdomínio",
                "Pratos e fotos ilimitados",
                "Códigos QR para mesas (PDF)",
                "Pedidos e mesas",
                "Análises e lista de esgotados",
                "Catálogo e mapa no app",
            ),
            cta = "Começar grátis",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/mês", null, null, true,
            lines = listOf(
                "Tudo do plano Free",
                "Programa de fidelidade com cashback",
                "Botão de chamada do garçom",
                "Conta do cliente no celular",
                "Cardápio em até 10 idiomas",
                "Seções «Popular» e «O chef recomenda»",
                "Convites para equipe e funcionários",
            ),
            cta = "Passar para o Pro",
        ),
    ),
    hotSticker = "Preferido dos locais",
    pricingFootnote = "Pagamento por período · cancele quando quiser · os dados são mantidos mesmo em pausa",
    faqTitle = "Perguntas e respostas",
    faqNote = "05 perguntas",
    faqs = listOf(
        LandingFaq(
            "Preciso de cartão bancário para começar?",
            "Não. O plano Free não precisa de cartão — seu cardápio funciona grátis e sem limite de tempo.",
        ),
        LandingFaq(
            "O que acontece quando a assinatura termina?",
            "Seu site-cardápio continua funcionando no plano Free; só as funções Pro pausam (fidelidade, " +
                "chamada do garçom, conta, idiomas extras, convites). Todos os dados são mantidos — " +
                "pague e tudo volta na hora.",
        ),
        LandingFaq(
            "Posso ter o cardápio em vários idiomas?",
            "Sim, você pode adicionar até 10 idiomas ao cardápio. O cliente troca de idioma com um toque.",
        ),
        LandingFaq(
            "Como os clientes encontram meu lugar no app?",
            "Todos os estabelecimentos ativos aparecem automaticamente no catálogo e no mapa do app QRkontaq — " +
                "com cardápio, nota e rota.",
        ),
        LandingFaq(
            "Tenho vários estabelecimentos. Dá certo?",
            "Sim, de uma conta você gerencia vários estabelecimentos e alterna entre eles no painel.",
        ),
    ),
    ctaSticker = "Sem cartão",
    ctaTitle = "Crie seu cardápio QR hoje",
    ctaSub = "Plano grátis para sempre. Em 1 minuto seus clientes já estarão folheando o cardápio com fotos.",
    ctaBtn1 = "Criar site grátis",
    ctaBtn2 = "Ver preços",
    footerTag = "Cardápio QR e mini-site para cafés, restaurantes e bares. Criado em uma noite, funciona todo dia.",
    footerContacts = "Contatos",
    footerMade = "Feito com ♥ para cafés e restaurantes",
    footerTerms = "Termos",
    footerPrivacy = "Privacidade",
    footerRefund = "Reembolsos",
)

/* -------------------------------- Français --------------------------------- */

private val landingFr = LandingStrings(
    seoTitle = "QRkontaq : menu QR pour cafés et restaurants",
    seoDescription = "Menu QR numérique avec photos pour votre établissement : site sur sous-domaine dédié, " +
        "QR codes pour les tables, stop-liste et statistiques. Offre gratuite pour toujours, sans carte.",
    navHow = "Comment ça marche",
    navFeatures = "Fonctionnalités",
    navPricing = "Tarifs",
    navFaq = "FAQ",
    navLogin = "Connexion",
    navCta = "Créer un site en 1 minute",
    navCtaShort = "Site en 1 minute",
    heroBadge = "Créez votre site en 1 minute — et utilisez-le aussitôt",
    heroTitle = listOf(
        HeroWord("Un"), HeroWord("menu"), HeroWord("QR"), HeroWord("que"), HeroWord("vos"),
        HeroWord("clients", gradient = true), HeroWord("adorent", gradient = true),
        HeroWord("scanner"),
    ),
    heroSub = "Le client scanne le code sur la table — et découvre un beau menu avec photos à l'adresse de votre établissement. " +
        "Sans développeurs, sans applications, sans réimprimer les menus chaque saison.",
    heroCtaTrial = "Créez votre site en 1 minute",
    heroCtaHow = "Comment ça marche ↓",
    heroNotes = listOf(
        HeroNote("", "Sans carte", " pour commencer"),
        HeroNote("Le site fonctionne ", "immédiatement", ""),
        HeroNote("", "1 minute", " avant le premier scan"),
    ),
    phoneSticker = "Offre gratuite",
    pillOpen = "Ouvert",
    pillLangs = "10 langues",
    chips = listOf("Tout", "Pizza", "Pâtes", "Desserts"),
    dishes = listOf(
        MockDishData("pizza", "Pizza Margherita", "tomates · mozzarella · basilic", "€8,90", "450 g · 780 kcal"),
        MockDishData("pasta", "Pâtes Carbonara", "guanciale · pecorino", "€10,50", "320 g · 640 kcal"),
        MockDishData("dessert", "Tiramisu", "mascarpone · expresso", "€6,50", "140 g · 320 kcal"),
        MockDishData("drink", "Limonade agrumes", "orange · menthe", "€4,20", "330 ml · 90 kcal"),
    ),
    mockSection = "Populaires",
    mockHit = "Top",
    fcScan = "Scannez le menu",
    fcRating = "Note 4.9",
    fcRatingSub = "124 avis clients",
    fcStop = "Stop-liste",
    fcStopSub = "« Tiramisu » masqué en 1 sec",
    fcScans = "+186 scans",
    fcScansSub = "aujourd'hui",
    mockWaiter = "Serveur",
    mockCheck = "L'addition",
    marquee = listOf(
        "Menu en 1 minute", "Sous-domaine dédié", "QR codes en PDF",
        "Rupture en un geste", "Statistiques de scans", "Jusqu'à 10 langues", "Catalogue et carte",
    ),
    stepsOverline = "Comment ça marche",
    stepsTitle = "De l'inscription au premier scan",
    stepsNote = "03 étapes",
    stepsSub = "Pas de code, de designers ni de longs réglages. Tout tient en une soirée — souvent en un café.",
    steps = listOf(
        LandingStep("Inscrivez-vous et nommez votre établissement", "Choisissez l'adresse du site — elle fonctionne immédiatement, avant même que votre café soit prêt."),
        LandingStep("Ajoutez le menu et votre marque", "Logo, couleurs, catégories et plats avec photos. Le menu ressemble à votre propre site."),
        LandingStep("Imprimez les QR codes", "Un PDF prêt pour chaque table. Le client scanne — et feuillette déjà le menu avec photos."),
    ),
    ghostWord = "menu",
    featuresOverline = "Fonctionnalités",
    featuresTitle = "Tout ce qu'il faut à votre établissement",
    featuresNote = "05 positions",
    featuresSub = "Menu, marque, équipe et statistiques — dans un espace que tout serveur maîtrisera.",
    tileSubdomainTitle = "Sous-domaine dédié — immédiatement",
    tileSubdomainText = "Votre site vit à l'adresse bella-vista.qrkontaq.com dès la première minute après l'inscription. " +
        "Ni hébergement, ni bureau d'enregistrement.",
    tileBrandTitle = "Votre marque",
    tileBrandText = "Logo, couverture et couleur de marque — le menu ressemble à votre propre site.",
    stopTag = "Épuisé",
    tileTeamTitle = "Rôles pour l'équipe",
    tileTeamText = "Propriétaire, manager, employé — chacun ne voit que ce qui le concerne.",
    tileAppTitle = "L'app QRkontaq amène de nouveaux clients",
    tileAppText = "Votre établissement apparaît automatiquement dans le catalogue et sur la carte de l'app — " +
        "avec menu, note et itinéraire. Les clients à proximité vous trouvent d'eux-mêmes.",
    urlHint = "le nom de votre établissement",
    adminTitle = "Un espace que l'équipe maîtrise en 10 minutes",
    adminSub = "Tout se gère depuis un téléphone ou un ordinateur : vous modifiez — et une seconde plus tard, " +
        "les clients voient déjà le nouveau menu. Voici l'intérieur.",
    adminTabs = listOf(
        AdminTabData("Menu", "Modifiez le menu en quelques secondes", "Ajoutez plats, photos et prix sans designer. Un plat épuisé ? Stop-liste en un geste — chaque changement apparaît aussitôt."),
        AdminTabData("Commandes", "La cuisine en temps réel", "Nouveau — en cuisine — prêt. Chaque commande avance sur le tableau et toute l'équipe suit les statuts en direct."),
        AdminTabData("Tables", "Tables, appels et QR codes", "Le plan de salle affiche les appels serveur en direct, et un PDF de QR prêt à imprimer est à un clic."),
        AdminTabData("Aperçu", "Des statistiques sans tableurs", "Scans, plats favoris et note sur un seul écran. Voyez ce que vos clients aiment vraiment."),
    ),
    adminAddDish = "+ Ajouter un plat",
    adminTopDish = "Plat vedette",
    adminTable = "Table",
    adminDownloadPdf = "Télécharger le PDF",
    adminRole = "Propriétaire",
    adminOpenSite = "Ouvrir le site",
    adminStopList = "Stop-liste",
    adminNavTeam = "Équipe",
    adminNavSettings = "Réglages",
    adminNavBilling = "Abonnement",
    adminLanes = listOf("Nouveau", "En cuisine", "Prêt"),
    adminCall = "Appel serveur",
    reviewsOverline = "Avis",
    reviewsTitle = "Ce qu'en disent les établissements",
    stats = listOf(
        StatData("5418", "", "cafés et restaurants déjà avec nous"),
        StatData("1", " min", "de l'inscription au menu prêt"),
        StatData("0", " €", "l'offre Free — votre menu en ligne pour toujours"),
        StatData("10", "", "langues de menu pour vos clients"),
        StatData(null, "∞", "plats, photos et mises à jour"),
    ),
    pricingOverline = "Tarifs",
    pricingTitle = "Free ou Pro",
    pricingNote = "02 offres",
    pricingSub = "Le menu est gratuit pour toujours. Pro débloque les services clients et votre équipe — €14.99 par mois.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/mois", null, null, false,
            lines = listOf(
                "Site-menu sur votre sous-domaine",
                "Plats et photos illimités",
                "QR codes pour les tables (PDF)",
                "Commandes et tables",
                "Statistiques et stop-liste",
                "Catalogue et carte dans l'app",
            ),
            cta = "Commencer gratuitement",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/mois", null, null, true,
            lines = listOf(
                "Tout ce qui est dans Free",
                "Programme de fidélité avec cashback",
                "Bouton d'appel du serveur",
                "Addition du client sur le téléphone",
                "Menu en 10 langues maximum",
                "Sections « Populaire » et « Le chef recommande »",
                "Invitations pour l'équipe et le personnel",
            ),
            cta = "Passer à Pro",
        ),
    ),
    hotSticker = "Choix des restaurateurs",
    pricingFootnote = "Paiement par période · annulable à tout moment · vos données sont conservées même en pause",
    faqTitle = "Questions et réponses",
    faqNote = "05 questions",
    faqs = listOf(
        LandingFaq(
            "Faut-il une carte bancaire pour commencer ?",
            "Non. L'offre Free ne demande aucune carte — votre menu fonctionne gratuitement, sans limite de durée.",
        ),
        LandingFaq(
            "Que se passe-t-il à la fin de l'abonnement ?",
            "Votre site-menu continue de fonctionner avec l'offre Free ; seules les fonctions Pro se mettent " +
                "en pause (fidélité, appel serveur, addition, langues supplémentaires, invitations). " +
                "Toutes les données sont conservées — payez, et tout revient aussitôt.",
        ),
        LandingFaq(
            "Le menu peut-il être multilingue ?",
            "Oui, vous pouvez ajouter jusqu'à 10 langues au menu. Le client change de langue d'un geste.",
        ),
        LandingFaq(
            "Comment les clients trouvent-ils mon établissement dans l'app ?",
            "Tous les établissements actifs apparaissent automatiquement dans le catalogue et sur la carte " +
                "de l'app QRkontaq — avec menu, note et itinéraire.",
        ),
        LandingFaq(
            "J'ai plusieurs établissements. Est-ce possible ?",
            "Oui, un seul compte permet de gérer plusieurs établissements et de passer de l'un à l'autre.",
        ),
    ),
    ctaSticker = "Sans carte",
    ctaTitle = "Créez votre menu QR aujourd'hui",
    ctaSub = "Offre gratuite pour toujours. En 1 minute, vos clients feuilletteront déjà un menu avec photos.",
    ctaBtn1 = "Créer un site gratuitement",
    ctaBtn2 = "Voir les tarifs",
    footerTag = "Menu QR et mini-site pour cafés, restaurants et bars. Créé en une soirée, opérationnel chaque jour.",
    footerContacts = "Contacts",
    footerMade = "Fait avec ♥ pour les cafés et restaurants",
    footerTerms = "Conditions",
    footerPrivacy = "Confidentialité",
    footerRefund = "Remboursements",
)

/* -------------------------------- Українська -------------------------------- */

private val landingUk = LandingStrings(
    seoTitle = "QRkontaq: QR-меню для кафе і ресторанів",
    seoDescription = "Електронне QR-меню з фото для вашого закладу: власний сайт на поддомені, QR-коди для столів, " +
        "стоп-лист і аналітика. Безкоштовний план назавжди, без картки.",
    navHow = "Як це працює",
    navFeatures = "Можливості",
    navPricing = "Тарифи",
    navFaq = "FAQ",
    navLogin = "Увійти",
    navCta = "Створити сайт за 1 хвилину",
    navCtaShort = "Сайт за 1 хвилину",
    heroBadge = "Створіть власний сайт за 1 хвилину — і одразу користуйтеся",
    heroTitle = listOf(
        HeroWord("QR-меню,"), HeroWord("яке"),
        HeroWord("гості", gradient = true), HeroWord("обожнюють", gradient = true),
        HeroWord("сканувати"),
    ),
    heroSub = "Гість сканує код на столі — і бачить красиве меню з фото на адресі вашого закладу. " +
        "Без розробників, без застосунків, без друку нових меню щосезону.",
    heroCtaTrial = "Створити сайт за 1 хвилину",
    heroCtaHow = "Як це працює ↓",
    heroNotes = listOf(
        HeroNote("", "Без картки", " для старту"),
        HeroNote("Сайт працює ", "одразу", ""),
        HeroNote("", "1 хвилина", " до першого скану"),
    ),
    phoneSticker = "Безкоштовний план",
    pillOpen = "Відчинено",
    pillLangs = "10 мов",
    chips = listOf("Все", "Піца", "Паста", "Десерти"),
    dishes = listOf(
        MockDishData("pizza", "Піца Маргарита", "томати · моцарела · базилік", "€8.90", "450 г · 780 ккал"),
        MockDishData("pasta", "Паста Карбонара", "гуанчале · пекорино", "€10.50", "320 г · 640 ккал"),
        MockDishData("dessert", "Тірамісу", "маскарпоне · еспресо", "€6.50", "140 г · 320 ккал"),
        MockDishData("drink", "Цитрусовий лимонад", "апельсин · м'ята", "€4.20", "330 мл · 90 ккал"),
    ),
    mockSection = "Популярне",
    mockHit = "Хіт",
    fcScan = "Скануйте меню",
    fcRating = "4.9 рейтинг",
    fcRatingSub = "124 відгуки гостей",
    fcStop = "Стоп-лист",
    fcStopSub = "«Тірамісу» вимкнено за 1 сек",
    fcScans = "+186 сканів",
    fcScansSub = "сьогодні",
    mockWaiter = "Офіціант",
    mockCheck = "Мій рахунок",
    marquee = listOf(
        "Меню за 1 хвилину", "Власний поддомен", "QR-коди у PDF",
        "Стоп-лист одним дотиком", "Аналітика сканів", "До 10 мов", "Каталог і мапа",
    ),
    stepsOverline = "Як це працює",
    stepsTitle = "Від реєстрації до першого скану",
    stepsNote = "03 кроки",
    stepsSub = "Жодного коду, дизайнерів чи довгих налаштувань. Все займає один вечір — а частіше одну каву.",
    steps = listOf(
        LandingStep("Зареєструйтесь і назвіть заклад", "Оберіть адресу сайту — вона запрацює миттєво, ще до того, як ви наллєте собі каву."),
        LandingStep("Додайте меню та бренд", "Логотип, кольори, категорії і страви з фото. Меню виглядає як ваш власний сайт."),
        LandingStep("Роздрукуйте QR-коди", "Готовий PDF для кожного столу. Гість сканує — і вже гортає меню з фото."),
    ),
    ghostWord = "меню",
    featuresOverline = "Можливості",
    featuresTitle = "Все, що потрібно закладу",
    featuresNote = "05 позицій",
    featuresSub = "Меню, бренд, команда й аналітика — в одному кабінеті, який опанує будь-який офіціант.",
    tileSubdomainTitle = "Власний поддомен — одразу",
    tileSubdomainText = "Ваш сайт живе на адресі bella-vista.qrkontaq.com з першої хвилини після реєстрації. " +
        "Жодних хостингів і доменних реєстраторів.",
    tileBrandTitle = "Ваш бренд",
    tileBrandText = "Логотип, обкладинка та фірмовий колір — меню виглядає як ваш власний сайт.",
    stopTag = "В стопі",
    tileTeamTitle = "Ролі для команди",
    tileTeamText = "Власник, менеджер, співробітник — кожен бачить лише своє.",
    tileAppTitle = "Застосунок QRkontaq приводить нових гостей",
    tileAppText = "Ваш заклад автоматично з'являється в каталозі та на мапі застосунку — з меню, рейтингом " +
        "і маршрутом. Гості поруч самі вас знаходять.",
    urlHint = "назва вашого закладу",
    adminTitle = "Кабінет, який опанують за 10 хвилин",
    adminSub = "Все керується з телефона чи ноутбука: змінили — і за секунду гості вже бачать нове меню. " +
        "Ось як це виглядає зсередини.",
    adminTabs = listOf(
        AdminTabData("Меню", "Меню редагується за секунди", "Додавайте страви, фото та ціни без дизайнера. Страва закінчилась? Стоп-лист одним дотиком — зміни миттєво на сайті."),
        AdminTabData("Замовлення", "Кухня в реальному часі", "Нове — готується — готово. Кожне замовлення рухається дошкою, і вся команда бачить статуси наживо."),
        AdminTabData("Столи", "Столи, виклики та QR-коди", "План зали показує виклики офіціанта наживо, а готовий PDF із QR для кожного столу — в один клік."),
        AdminTabData("Огляд", "Аналітика без таблиць", "Скани, топ страв і рейтинг — на одному екрані. Ви бачите, що гості люблять насправді."),
    ),
    adminAddDish = "+ Додати страву",
    adminTopDish = "Топ страва",
    adminTable = "Стіл",
    adminDownloadPdf = "Завантажити PDF",
    adminRole = "Власник",
    adminOpenSite = "Відкрити сайт",
    adminStopList = "Стоп-лист",
    adminNavTeam = "Команда",
    adminNavSettings = "Налаштування",
    adminNavBilling = "Підписка",
    adminLanes = listOf("Нове", "Готується", "Готово"),
    adminCall = "Виклик офіціанта",
    reviewsOverline = "Відгуки",
    reviewsTitle = "Що кажуть заклади про QRkontaq",
    stats = listOf(
        StatData("5418", "", "кафе та ресторанів уже з нами"),
        StatData("1", " хв", "від реєстрації до готового меню"),
        StatData("0", " €", "план Free — ваше меню онлайн назавжди"),
        StatData("10", "", "мов меню для ваших гостей"),
        StatData(null, "∞", "страв, фото та оновлень меню"),
    ),
    pricingOverline = "Тарифи",
    pricingTitle = "Free чи Pro",
    pricingNote = "02 плани",
    pricingSub = "Меню безкоштовне назавжди. Pro відкриває сервіси для гостей і команду — €14.99 на місяць.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/міс", null, null, false,
            lines = listOf(
                "Сайт-меню на власному поддомені",
                "Необмежені страви й фото",
                "QR-коди для столів (PDF)",
                "Замовлення та столи",
                "Аналітика та стоп-лист",
                "Каталог і мапа в застосунку",
            ),
            cta = "Почати безкоштовно",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/міс", null, null, true,
            lines = listOf(
                "Все з плану Free",
                "Програма лояльності з кешбеком",
                "Кнопка виклику офіціанта",
                "Рахунок гостя в телефоні",
                "Меню до 10 мов",
                "Розділи «Популярне» і «Шеф рекомендує»",
                "Запрошення для команди та персоналу",
            ),
            cta = "Перейти на Pro",
        ),
    ),
    hotSticker = "Хіт закладів",
    pricingFootnote = "Оплата раз на період · скасувати можна будь-коли · дані зберігаються навіть після паузи",
    faqTitle = "Питання та відповіді",
    faqNote = "05 питань",
    faqs = listOf(
        LandingFaq(
            "Чи потрібна банківська картка для старту?",
            "Ні. Для плану Free картка не потрібна — ваше меню працює безкоштовно без обмежень у часі.",
        ),
        LandingFaq(
            "Що буде, коли закінчиться підписка?",
            "Ваш сайт-меню продовжує працювати на плані Free; призупиняються лише Pro-функції (лояльність, " +
                "виклик офіціанта, рахунок, додаткові мови, запрошення). Всі дані зберігаються — " +
                "оплатіть, і все миттєво повернеться.",
        ),
        LandingFaq(
            "Чи можна вести меню кількома мовами?",
            "Так, до меню можна додати до 10 мов. Гість перемикає мову одним дотиком.",
        ),
        LandingFaq(
            "Як гості знаходять мій заклад у застосунку?",
            "Всі активні заклади автоматично з'являються в каталозі та на мапі застосунку QRkontaq — " +
                "з меню, рейтингом і маршрутом.",
        ),
        LandingFaq(
            "Я маю кілька закладів. Це підтримується?",
            "Так, з одного акаунта можна керувати кількома закладами та перемикатися між ними в кабінеті.",
        ),
    ),
    ctaSticker = "Без картки",
    ctaTitle = "Створіть QR-меню сьогодні",
    ctaSub = "Безкоштовний план назавжди. Через 1 хвилину ваші гості вже гортатимуть меню з фото.",
    ctaBtn1 = "Створити сайт безкоштовно",
    ctaBtn2 = "Подивитись тарифи",
    footerTag = "QR-меню та міні-сайт для кафе, ресторанів і барів. Створюється за вечір, працює щодня.",
    footerContacts = "Контакти",
    footerMade = "Зроблено з ♥ для закладів України",
    footerTerms = "Умови",
    footerPrivacy = "Конфіденційність",
    footerRefund = "Повернення коштів",
)

/* --------------------------------- Русский ---------------------------------- */

private val landingRu = LandingStrings(
    seoTitle = "QRkontaq: QR-меню для кафе и ресторанов",
    seoDescription = "Электронное QR-меню с фото для вашего заведения: собственный сайт на поддомене, " +
        "QR-коды для столов, стоп-лист и аналитика. Бесплатный план навсегда, без карты.",
    navHow = "Как это работает",
    navFeatures = "Возможности",
    navPricing = "Тарифы",
    navFaq = "FAQ",
    navLogin = "Войти",
    navCta = "Создать сайт за 1 минуту",
    navCtaShort = "Сайт за 1 минуту",
    heroBadge = "Создайте свой сайт за 1 минуту — и сразу пользуйтесь",
    heroTitle = listOf(
        HeroWord("QR-меню,"), HeroWord("которое"),
        HeroWord("гости", gradient = true), HeroWord("обожают", gradient = true),
        HeroWord("сканировать"),
    ),
    heroSub = "Гость сканирует код на столе — и видит красивое меню с фото на адресе вашего заведения. " +
        "Без разработчиков, без приложений, без печати новых меню каждый сезон.",
    heroCtaTrial = "Создать сайт за 1 минуту",
    heroCtaHow = "Как это работает ↓",
    heroNotes = listOf(
        HeroNote("", "Без карты", " для старта"),
        HeroNote("Сайт работает ", "сразу", ""),
        HeroNote("", "1 минута", " до первого скана"),
    ),
    phoneSticker = "Бесплатный план",
    pillOpen = "Открыто",
    pillLangs = "10 языков",
    chips = listOf("Все", "Пицца", "Паста", "Десерты"),
    dishes = listOf(
        MockDishData("pizza", "Пицца Маргарита", "томаты · моцарелла · базилик", "€8.90", "450 г · 780 ккал"),
        MockDishData("pasta", "Паста Карбонара", "гуанчиале · пекорино", "€10.50", "320 г · 640 ккал"),
        MockDishData("dessert", "Тирамису", "маскарпоне · эспрессо", "€6.50", "140 г · 320 ккал"),
        MockDishData("drink", "Цитрусовый лимонад", "апельсин · мята", "€4.20", "330 мл · 90 ккал"),
    ),
    mockSection = "Популярное",
    mockHit = "Хит",
    fcScan = "Сканируйте меню",
    fcRating = "4.9 рейтинг",
    fcRatingSub = "124 отзыва гостей",
    fcStop = "Стоп-лист",
    fcStopSub = "«Тирамису» выключено за 1 сек",
    fcScans = "+186 сканов",
    fcScansSub = "сегодня",
    mockWaiter = "Официант",
    mockCheck = "Мой счёт",
    marquee = listOf(
        "Меню за 1 минуту", "Собственный поддомен", "QR-коды в PDF",
        "Стоп-лист одним касанием", "Аналитика сканов", "До 10 языков", "Каталог и карта",
    ),
    stepsOverline = "Как это работает",
    stepsTitle = "От регистрации до первого скана",
    stepsNote = "03 шага",
    stepsSub = "Никакого кода, дизайнеров и долгих настроек. Всё занимает один вечер — а чаще один кофе.",
    steps = listOf(
        LandingStep("Зарегистрируйтесь и назовите заведение", "Выберите адрес сайта — он заработает мгновенно, ещё до того, как вы нальёте себе кофе."),
        LandingStep("Добавьте меню и бренд", "Логотип, цвета, категории и блюда с фото. Меню выглядит как ваш собственный сайт."),
        LandingStep("Распечатайте QR-коды", "Готовый PDF для каждого стола. Гость сканирует — и уже листает меню с фото."),
    ),
    ghostWord = "меню",
    featuresOverline = "Возможности",
    featuresTitle = "Всё, что нужно заведению",
    featuresNote = "05 позиций",
    featuresSub = "Меню, бренд, команда и аналитика — в одном кабинете, который освоит любой официант.",
    tileSubdomainTitle = "Собственный поддомен — сразу",
    tileSubdomainText = "Ваш сайт живёт на адресе bella-vista.qrkontaq.com с первой минуты после регистрации. " +
        "Никаких хостингов и регистраторов доменов.",
    tileBrandTitle = "Ваш бренд",
    tileBrandText = "Логотип, обложка и фирменный цвет — меню выглядит как ваш собственный сайт.",
    stopTag = "В стопе",
    tileTeamTitle = "Роли для команды",
    tileTeamText = "Владелец, менеджер, сотрудник — каждый видит только своё.",
    tileAppTitle = "Приложение QRkontaq приводит новых гостей",
    tileAppText = "Ваше заведение автоматически появляется в каталоге и на карте приложения — с меню, рейтингом " +
        "и маршрутом. Гости рядом сами вас находят.",
    urlHint = "название вашего заведения",
    adminTitle = "Кабинет, который освоят за 10 минут",
    adminSub = "Всё управляется с телефона или ноутбука: изменили — и через секунду гости уже видят новое меню. " +
        "Вот как это выглядит изнутри.",
    adminTabs = listOf(
        AdminTabData("Меню", "Меню редактируется за секунды", "Добавляйте блюда, фото и цены без дизайнера. Блюдо закончилось? Стоп-лист одним касанием — изменения мгновенно на сайте."),
        AdminTabData("Заказы", "Кухня в реальном времени", "Новый — готовится — готов. Каждый заказ движется по доске, и вся команда видит статусы вживую."),
        AdminTabData("Столы", "Столы, вызовы и QR-коды", "План зала показывает вызовы официанта вживую, а готовый PDF с QR для каждого стола — в один клик."),
        AdminTabData("Обзор", "Аналитика без таблиц", "Сканы, топ блюд и рейтинг — на одном экране. Вы видите, что гости любят на самом деле."),
    ),
    adminAddDish = "+ Добавить блюдо",
    adminTopDish = "Топ блюдо",
    adminTable = "Стол",
    adminDownloadPdf = "Скачать PDF",
    adminRole = "Владелец",
    adminOpenSite = "Открыть сайт",
    adminStopList = "Стоп-лист",
    adminNavTeam = "Команда",
    adminNavSettings = "Настройки",
    adminNavBilling = "Подписка",
    adminLanes = listOf("Новый", "Готовится", "Готов"),
    adminCall = "Вызов официанта",
    reviewsOverline = "Отзывы",
    reviewsTitle = "Что говорят заведения о QRkontaq",
    stats = listOf(
        StatData("5418", "", "кафе и ресторанов уже с нами"),
        StatData("1", " мин", "от регистрации до готового меню"),
        StatData("0", " €", "план Free — ваше меню онлайн навсегда"),
        StatData("10", "", "языков меню для ваших гостей"),
        StatData(null, "∞", "блюд, фото и обновлений меню"),
    ),
    pricingOverline = "Тарифы",
    pricingTitle = "Free или Pro",
    pricingNote = "02 плана",
    pricingSub = "Меню бесплатно навсегда. Pro открывает сервисы для гостей и команду — €14.99 в месяц.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/мес", null, null, false,
            lines = listOf(
                "Сайт-меню на собственном поддомене",
                "Неограниченные блюда и фото",
                "QR-коды для столов (PDF)",
                "Заказы и столы",
                "Аналитика и стоп-лист",
                "Каталог и карта в приложении",
            ),
            cta = "Начать бесплатно",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/мес", null, null, true,
            lines = listOf(
                "Всё из плана Free",
                "Программа лояльности с кешбэком",
                "Кнопка вызова официанта",
                "Счёт гостя в телефоне",
                "Меню до 10 языков",
                "Разделы «Популярное» и «Шеф рекомендует»",
                "Приглашения для команды и персонала",
            ),
            cta = "Перейти на Pro",
        ),
    ),
    hotSticker = "Хит заведений",
    pricingFootnote = "Оплата раз в период · отменить можно в любой момент · данные сохраняются даже после паузы",
    faqTitle = "Вопросы и ответы",
    faqNote = "05 вопросов",
    faqs = listOf(
        LandingFaq(
            "Нужна ли банковская карта для старта?",
            "Нет. Для плана Free карта не нужна — ваше меню работает бесплатно без ограничений по времени.",
        ),
        LandingFaq(
            "Что будет, когда закончится подписка?",
            "Ваш сайт-меню продолжает работать на плане Free; приостанавливаются только Pro-функции (лояльность, " +
                "вызов официанта, счёт, дополнительные языки, приглашения). Все данные сохраняются — " +
                "оплатите, и всё мгновенно вернётся.",
        ),
        LandingFaq(
            "Можно ли вести меню на нескольких языках?",
            "Да, в меню можно добавить до 10 языков. Гость переключает язык одним касанием.",
        ),
        LandingFaq(
            "Как гости находят моё заведение в приложении?",
            "Все активные заведения автоматически появляются в каталоге и на карте приложения QRkontaq — " +
                "с меню, рейтингом и маршрутом.",
        ),
        LandingFaq(
            "У меня несколько заведений. Это поддерживается?",
            "Да, с одного аккаунта можно управлять несколькими заведениями и переключаться между ними в кабинете.",
        ),
    ),
    ctaSticker = "Без карты",
    ctaTitle = "Создайте QR-меню сегодня",
    ctaSub = "Бесплатный план навсегда. Через 1 минуту ваши гости уже будут листать меню с фото.",
    ctaBtn1 = "Создать сайт бесплатно",
    ctaBtn2 = "Посмотреть тарифы",
    footerTag = "QR-меню и мини-сайт для кафе, ресторанов и баров. Создаётся за вечер, работает каждый день.",
    footerContacts = "Контакты",
    footerMade = "Сделано с ♥ для кафе и ресторанов",
    footerTerms = "Условия",
    footerPrivacy = "Конфиденциальность",
    footerRefund = "Возврат средств",
)

/* ---------------------------------- Deutsch --------------------------------- */

private val landingDe = LandingStrings(
    seoTitle = "QRkontaq: QR-Menü für Cafés und Restaurants",
    seoDescription = "Digitales QR-Menü mit Fotos für Ihr Lokal: eigene Subdomain-Website, QR-Codes für Tische, " +
        "Stopp-Liste und Statistiken. Gratis-Tarif für immer, ohne Karte.",
    navHow = "So funktioniert's",
    navFeatures = "Funktionen",
    navPricing = "Preise",
    navFaq = "FAQ",
    navLogin = "Anmelden",
    navCta = "Website in 1 Minute erstellen",
    navCtaShort = "Website in 1 Minute",
    heroBadge = "Erstellen Sie Ihre Website in 1 Minute — und nutzen Sie sie sofort",
    heroTitle = listOf(
        HeroWord("Ein"), HeroWord("QR-Menü,"), HeroWord("das"),
        HeroWord("Gäste", gradient = true), HeroWord("gerne", gradient = true),
        HeroWord("scannen"),
    ),
    heroSub = "Der Gast scannt den Code am Tisch — und sieht ein schönes Menü mit Fotos unter der Adresse Ihres Lokals. " +
        "Ohne Entwickler, ohne Apps, ohne saisonalen Neudruck der Speisekarten.",
    heroCtaTrial = "Website in 1 Minute erstellen",
    heroCtaHow = "So funktioniert's ↓",
    heroNotes = listOf(
        HeroNote("", "Keine Karte", " zum Start nötig"),
        HeroNote("Die Website läuft ", "sofort", ""),
        HeroNote("", "1 Minute", " bis zum ersten Scan"),
    ),
    phoneSticker = "Gratis-Tarif",
    pillOpen = "Geöffnet",
    pillLangs = "10 Sprachen",
    chips = listOf("Alle", "Pizza", "Pasta", "Desserts"),
    dishes = listOf(
        MockDishData("pizza", "Pizza Margherita", "Tomaten · Mozzarella · Basilikum", "€8,90", "450 g · 780 kcal"),
        MockDishData("pasta", "Pasta Carbonara", "Guanciale · Pecorino", "€10,50", "320 g · 640 kcal"),
        MockDishData("dessert", "Tiramisu", "Mascarpone · Espresso", "€6,50", "140 g · 320 kcal"),
        MockDishData("drink", "Zitrus-Limonade", "Orange · Minze", "€4,20", "330 ml · 90 kcal"),
    ),
    mockSection = "Beliebt",
    mockHit = "Hit",
    fcScan = "Menü scannen",
    fcRating = "4.9 Bewertung",
    fcRatingSub = "124 Gästebewertungen",
    fcStop = "Stopp-Liste",
    fcStopSub = "„Tiramisu“ in 1 Sek. deaktiviert",
    fcScans = "+186 Scans",
    fcScansSub = "heute",
    mockWaiter = "Kellner",
    mockCheck = "Rechnung",
    marquee = listOf(
        "Menü in 1 Minute", "Eigene Subdomain", "QR-Codes als PDF",
        "Stopp-Liste mit einem Tipp", "Scan-Statistiken", "Bis zu 10 Sprachen", "Katalog & Karte",
    ),
    stepsOverline = "So funktioniert's",
    stepsTitle = "Von der Registrierung zum ersten Scan",
    stepsNote = "03 Schritte",
    stepsSub = "Kein Code, keine Designer, keine langen Einstellungen. Alles dauert einen Abend — meist nur einen Kaffee.",
    steps = listOf(
        LandingStep("Registrieren und Lokal benennen", "Wählen Sie die Website-Adresse — sie ist sofort online, noch bevor Ihr Kaffee fertig ist."),
        LandingStep("Menü und Marke hinzufügen", "Logo, Farben, Kategorien und Gerichte mit Fotos. Das Menü wirkt wie Ihre eigene Website."),
        LandingStep("QR-Codes ausdrucken", "Ein fertiges PDF für jeden Tisch. Der Gast scannt — und blättert schon im Menü mit Fotos."),
    ),
    ghostWord = "Menü",
    featuresOverline = "Funktionen",
    featuresTitle = "Alles, was Ihr Lokal braucht",
    featuresNote = "05 Positionen",
    featuresSub = "Menü, Marke, Team und Statistiken — in einem Dashboard, das jeder Kellner beherrscht.",
    tileSubdomainTitle = "Eigene Subdomain — sofort",
    tileSubdomainText = "Ihre Website lebt unter bella-vista.qrkontaq.com ab der ersten Minute nach der Registrierung. " +
        "Kein Hosting, keine Domain-Registrare.",
    tileBrandTitle = "Ihre Marke",
    tileBrandText = "Logo, Cover und Markenfarbe — das Menü wirkt wie Ihre eigene Website.",
    stopTag = "Im Stopp",
    tileTeamTitle = "Rollen fürs Team",
    tileTeamText = "Inhaber, Manager, Mitarbeiter — jeder sieht nur seinen Bereich.",
    tileAppTitle = "Die QRkontaq-App bringt neue Gäste",
    tileAppText = "Ihr Lokal erscheint automatisch im Katalog und auf der Karte der App — mit Menü, Bewertung " +
        "und Route. Gäste in der Nähe finden Sie von selbst.",
    urlHint = "der Name Ihres Lokals",
    adminTitle = "Ein Dashboard, das jeder in 10 Minuten beherrscht",
    adminSub = "Alles läuft vom Handy oder Laptop: etwas geändert — und eine Sekunde später sehen die Gäste " +
        "schon das neue Menü. So sieht es von innen aus.",
    adminTabs = listOf(
        AdminTabData("Menü", "Menü in Sekunden bearbeiten", "Gerichte, Fotos und Preise ohne Designer hinzufügen. Ausverkauft? Stopp-Liste mit einem Tipp — jede Änderung erscheint sofort."),
        AdminTabData("Bestellungen", "Die Küche in Echtzeit", "Neu — in Arbeit — fertig. Jede Bestellung wandert über das Board, das ganze Team sieht die Status live."),
        AdminTabData("Tische", "Tische, Rufe und QR-Codes", "Der Saalplan zeigt Kellnerrufe live, und ein druckfertiges QR-PDF für jeden Tisch ist einen Klick entfernt."),
        AdminTabData("Überblick", "Statistiken ohne Tabellen", "Scans, Top-Gerichte und Bewertung auf einem Bildschirm. Sehen Sie, was Ihre Gäste wirklich lieben."),
    ),
    adminAddDish = "+ Gericht hinzufügen",
    adminTopDish = "Top-Gericht",
    adminTable = "Tisch",
    adminDownloadPdf = "PDF herunterladen",
    adminRole = "Inhaber",
    adminOpenSite = "Website öffnen",
    adminStopList = "Stopp-Liste",
    adminNavTeam = "Team",
    adminNavSettings = "Einstellungen",
    adminNavBilling = "Abo",
    adminLanes = listOf("Neu", "In Arbeit", "Fertig"),
    adminCall = "Kellnerruf",
    reviewsOverline = "Bewertungen",
    reviewsTitle = "Was Lokale über QRkontaq sagen",
    stats = listOf(
        StatData("5418", "", "Cafés und Restaurants schon dabei"),
        StatData("1", " Min.", "von der Registrierung zum fertigen Menü"),
        StatData("0", " €", "der Free-Tarif — Ihr Menü für immer online"),
        StatData("10", "", "Menüsprachen für Ihre Gäste"),
        StatData(null, "∞", "Gerichte, Fotos und Updates"),
    ),
    pricingOverline = "Preise",
    pricingTitle = "Free oder Pro",
    pricingNote = "02 Pläne",
    pricingSub = "Das Menü ist für immer gratis. Pro schaltet Gäste-Services und Ihr Team frei — €14.99 im Monat.",
    plans = listOf(
        LandingPlan(
            "Free", "€0", null, "/Mon.", null, null, false,
            lines = listOf(
                "Menü-Website auf eigener Subdomain",
                "Unbegrenzte Gerichte und Fotos",
                "QR-Codes für Tische (PDF)",
                "Bestellungen und Tische",
                "Statistiken und Stopp-Liste",
                "Katalog und Karte in der App",
            ),
            cta = "Kostenlos starten",
        ),
        LandingPlan(
            "Pro", "€14.99", null, "/Mon.", null, null, true,
            lines = listOf(
                "Alles aus Free",
                "Treueprogramm mit Cashback",
                "Kellnerruf-Button",
                "Rechnung des Gastes am Handy",
                "Menü in bis zu 10 Sprachen",
                "Bereiche „Beliebt“ und „Chef empfiehlt“",
                "Einladungen für Team und Personal",
            ),
            cta = "Auf Pro upgraden",
        ),
    ),
    hotSticker = "Beliebt bei Lokalen",
    pricingFootnote = "Zahlung pro Zeitraum · jederzeit kündbar · Daten bleiben auch in der Pause erhalten",
    faqTitle = "Fragen und Antworten",
    faqNote = "05 Fragen",
    faqs = listOf(
        LandingFaq(
            "Brauche ich zum Start eine Bankkarte?",
            "Nein. Der Free-Tarif braucht keine Karte — Ihr Menü funktioniert gratis und ohne Zeitlimit.",
        ),
        LandingFaq(
            "Was passiert, wenn das Abo endet?",
            "Ihre Menü-Website läuft im Free-Tarif weiter; nur die Pro-Funktionen pausieren (Treueprogramm, " +
                "Kellnerruf, Rechnung, zusätzliche Sprachen, Einladungen). Alle Daten bleiben erhalten — " +
                "zahlen Sie, und alles ist sofort wieder da.",
        ),
        LandingFaq(
            "Kann das Menü mehrsprachig sein?",
            "Ja — Sie können bis zu 10 Sprachen zum Menü hinzufügen. Gäste wechseln die Sprache mit einem Tipp.",
        ),
        LandingFaq(
            "Wie finden Gäste mein Lokal in der App?",
            "Alle aktiven Lokale erscheinen automatisch im Katalog und auf der Karte der QRkontaq-App — " +
                "mit Menü, Bewertung und Route.",
        ),
        LandingFaq(
            "Ich habe mehrere Lokale. Geht das?",
            "Ja, mit einem Konto verwalten Sie mehrere Lokale und wechseln im Dashboard zwischen ihnen.",
        ),
    ),
    ctaSticker = "Ohne Karte",
    ctaTitle = "Erstellen Sie Ihr QR-Menü noch heute",
    ctaSub = "Gratis-Tarif für immer. In 1 Minute blättern Ihre Gäste schon im Menü mit Fotos.",
    ctaBtn1 = "Website kostenlos erstellen",
    ctaBtn2 = "Preise ansehen",
    footerTag = "QR-Menü und Mini-Website für Cafés, Restaurants und Bars. An einem Abend erstellt, jeden Tag im Einsatz.",
    footerContacts = "Kontakt",
    footerMade = "Mit ♥ gemacht für Cafés und Restaurants",
    footerTerms = "AGB",
    footerPrivacy = "Datenschutz",
    footerRefund = "Erstattungen",
)

/* --------------------------------- runtime ---------------------------------- */

internal enum class LandingLanguage(val code: String, val shortLabel: String, val nativeName: String) {
    ES("es", "ES", "Español"),
    EN("en", "EN", "English"),
    PT("pt", "PT", "Português"),
    FR("fr", "FR", "Français"),
    UK("uk", "UA", "Українська"),
    RU("ru", "RU", "Русский"),
    DE("de", "DE", "Deutsch"),
    ;

    val strings: LandingStrings
        get() = when (this) {
            ES -> landingEs
            EN -> landingEn
            PT -> landingPt
            FR -> landingFr
            UK -> landingUk
            RU -> landingRu
            DE -> landingDe
        }
}

private const val LANDING_LANGUAGE_KEY = "landing_language"

/**
 * A ?lang= query parameter wins over everything — these URLs are the indexable
 * per-language entry points listed in the sitemap/hreflang tags, so search
 * engines must always get the requested language regardless of stored state.
 */
internal fun landingUrlLanguage(): String? {
    val raw = URLSearchParams(window.location.search).get("lang")?.lowercase() ?: return null
    val code = if (raw == "ua") "uk" else raw
    return code.takeIf { c -> LandingLanguage.entries.any { it.code == c } }
}

/**
 * URL param wins; then a saved choice; then Spanish. The browser language is
 * deliberately ignored so every first-time visitor lands on the same (Spanish)
 * page — it also matches the language baked into the exported index.html, so
 * the static snapshot never flashes into a different language on boot.
 */
private fun detectLandingLanguage(): LandingLanguage {
    val supported = LandingLanguage.entries.map { it.code }
    val code =
        landingUrlLanguage()
            ?: Storage.get(LANDING_LANGUAGE_KEY)?.takeIf { it in supported }
            ?: LandingLanguage.ES.code
    return LandingLanguage.entries.first { it.code == code }
}

private val landingLanguageState = mutableStateOf(detectLandingLanguage())

internal val landingLanguage: LandingLanguage get() = landingLanguageState.value

/** Current landing strings; reading it inside a composable subscribes to language changes. */
internal val LT: LandingStrings get() = landingLanguageState.value.strings

internal fun setLandingLanguage(language: LandingLanguage) {
    Analytics.langSwitch(from = landingLanguageState.value.code, to = language.code)
    landingLanguageState.value = language
    Storage.save(LANDING_LANGUAGE_KEY, language.code)
}